import currency from 'currency.js'
import {
  ListPrice,
  PriceTableRow,
  PriceTierTableRow,
  VariantPrice
} from 'modules/Products/types'
import { match } from 'ts-pattern'
import {
  formatInteger,
  toMoney,
  toCurrencySymbol,
  decimalFromPercentage,
  toPriceSummary
} from '@sequencehq/utils'
import { Currency } from '@sequencehq/api'
import { Dashboardv20240730CommonModels } from '@sequencehq/api/dashboard/v20240730'
import { dateTimeWithFormat } from '@sequencehq/utils'

const percentageFormatter = (percentage: string) =>
  Number(decimalFromPercentage(percentage)).toLocaleString(undefined, {
    style: 'percent',
    minimumFractionDigits: 2
  })

export const getCurrencyValue = (symbol: Currency, price: string) =>
  currency(price, {
    symbol: toCurrencySymbol(symbol)
  }).value

export const pricingTypeLabel = (
  pricingType: Dashboardv20240730CommonModels.PricingStructure.PricingType
) => {
  if (pricingType === 'FIXED' || pricingType === 'ONE_TIME') {
    return 'Standard'
  }

  return pricingType
}

type PricingTier = {
  fee?: string
  isPricePercentage: boolean
  price: string
  upperBound?: string
}
export const priceTierRowFormatter = (
  tier: {
    fee?: string
    isPricePercentage: boolean
    price: string
    upperBound?: string
  },
  index: number,
  tiers: PricingTier[],
  price: {
    currency: Currency
  }
): PriceTierTableRow => {
  const currencySymbol = toCurrencySymbol(price.currency)
  const priceFormatter = (value: string) =>
    toMoney({ currency: price.currency, value, maximumFractionDigits: 10 })

  const previousTier = tiers[index - 1]
  const flatFee =
    Number(tier?.fee) > 0 ? priceFormatter(Number(tier.fee).toString()) : ''

  return match(tier)
    .with({ isPricePercentage: true }, () => {
      const firstUnit = previousTier?.upperBound
        ? currency(previousTier.upperBound, { symbol: currencySymbol })
            .add(0.01)
            .format()
        : priceFormatter('0')

      const lastUnit = tier?.upperBound ? priceFormatter(tier.upperBound) : '∞'

      const perUnit = percentageFormatter(tier.price)

      return { firstUnit, lastUnit, perUnit, flatFee }
    })
    .otherwise(() => {
      const firstUnit = previousTier?.upperBound
        ? formatInteger()(Number(previousTier.upperBound) + 1)
        : '0'

      const lastUnit = tier?.upperBound ? formatInteger()(tier.upperBound) : '∞'

      const perUnit = priceFormatter(tier.price)

      return { firstUnit, lastUnit, perUnit, flatFee }
    })
}

type PriceTypeDescription = {
  pricingType: Dashboardv20240730CommonModels.PricingStructure.PricingType
  isPricePercentage: boolean
  group: string
  label: string
  labelShort: string
  tableLabel: string
  description: string
}

export const pricingTypeDescriptions: Record<
  Dashboardv20240730CommonModels.PricingStructure.PricingType,
  PriceTypeDescription
> = {
  FIXED: {
    pricingType: 'FIXED',
    isPricePercentage: false,
    group: 'Fixed charge',
    label: 'Fixed pricing',
    labelShort: 'Fixed',
    tableLabel: 'Fixed',
    description:
      'Charge the same price for each unit. Choose between recurring and one-off charges.'
  },
  ONE_TIME: {
    pricingType: 'ONE_TIME',
    isPricePercentage: false,
    group: 'Fixed charge',
    label: 'Fixed pricing',
    labelShort: 'Charged once',
    tableLabel: 'Fixed',
    description: 'Charge a one-time fee (e.g. setup fee).'
  },
  LINEAR: {
    pricingType: 'LINEAR',
    isPricePercentage: false,
    group: 'Usage-based charge',
    label: 'Linear pricing',
    labelShort: 'Linear rate',
    tableLabel: 'Linear',
    description: 'Charge a single, linear rate for usage.'
  },
  PACKAGE: {
    pricingType: 'PACKAGE',
    isPricePercentage: false,
    group: 'Usage-based charge',
    label: 'Packaged pricing',
    labelShort: 'Packaged',
    tableLabel: 'Package',
    description: 'Charge a fixed fee for a packaged quantity of units.'
  },
  VOLUME: {
    pricingType: 'VOLUME',
    isPricePercentage: false,
    group: 'Usage-based charge',
    label: 'Volume pricing',
    labelShort: 'Volume tiers',
    tableLabel: 'Volume tiers',
    description: 'All usage is charged a single unit fee based on total volume.'
  },
  GRADUATED: {
    pricingType: 'GRADUATED',
    isPricePercentage: false,
    group: 'Usage-based charge',
    label: 'Graduated pricing',
    labelShort: 'Graduated tiers',
    tableLabel: 'Graduated tiers',
    description:
      'Usage in each tier is charged separately using respective tier fees.'
  },
  SEAT_BASED: {
    pricingType: 'SEAT_BASED',
    isPricePercentage: false,
    group: 'Seat-based charge',
    label: 'Seat-based pricing',
    labelShort: 'Per seat',
    tableLabel: 'Seat-based',
    description: 'Charge a per-seat fee and track the number of live seats.'
  }
}

export const toPricingTypeTableLabel = (
  value: Dashboardv20240730CommonModels.PricingStructure.PricingType,
  fallback?: string
): string => {
  return pricingTypeDescriptions[value]?.tableLabel ?? fallback ?? ''
}

export const priceRowFormatter = (
  price: ListPrice | VariantPrice
): PriceTableRow => ({
  id: price.id,
  name: price.name,
  frequency: price.billingFrequency,
  currency: price.currency,
  unitPrice: toPriceSummary(price),
  pricingModel: `${toPricingTypeTableLabel(
    price.structure.pricingType,
    pricingTypeLabel(price.structure.pricingType)
  )}`,
  created: dateTimeWithFormat(price.createdAt, 'dd MMM yyyy'),
  status: 'archivedAt' in price && price.archivedAt ? 'ARCHIVED' : 'ACTIVE'
})
